import React, { forwardRef } from 'react';
import ReactDatePicker from 'react-datepicker';
import { Calendar } from 'lucide-react';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './DatePicker.module.css';

interface DatePickerProps {
  value: string; // ISO date string (YYYY-MM-DD)
  onChange: (date: string) => void;
  className?: string;
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;
}

// Custom input component to match our styling with calendar icon
const CustomInput = forwardRef<HTMLInputElement, any>(({ value, onClick, placeholder, className }, ref) => (
  <div className={styles.inputWrapper} onClick={onClick}>
    <input
      ref={ref}
      type="text"
      value={value}
      placeholder={placeholder}
      className={className}
      readOnly
    />
    <Calendar className={styles.calendarIcon} size={16} />
  </div>
));

CustomInput.displayName = 'CustomInput';

export const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  className = '',
  placeholder = 'mm/dd/yyyy',
  minDate,
  maxDate,
}) => {
  // Convert ISO string to Date object
  // Parse the date string in local timezone to avoid timezone shift issues
  const selectedDate = value ? (() => {
    const [year, month, day] = value.split('-').map(Number);
    // Create date in local timezone (month is 0-indexed in JS Date)
    return new Date(year, month - 1, day);
  })() : null;

  // Handle date change
  const handleChange = (date: Date | null) => {
    if (date) {
      // Convert to ISO date string (YYYY-MM-DD)
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      onChange(`${year}-${month}-${day}`);
    } else {
      onChange('');
    }
  };

  return (
    <div className={styles.datePickerWrapper}>
      <ReactDatePicker
        selected={selectedDate}
        onChange={handleChange}
        customInput={<CustomInput className={className} placeholder={placeholder} />}
        dateFormat="MM/dd/yyyy"
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        yearDropdownItemNumber={50}
        scrollableYearDropdown
        minDate={minDate}
        maxDate={maxDate}
        portalId="datepicker-portal"
        popperClassName={styles.datePickerPopper}
        calendarClassName={styles.datePickerCalendar}
        wrapperClassName={styles.datePickerInputWrapper}
      />
    </div>
  );
};

